import { useCallback, useEffect, useState } from 'react';
import { useI18n, type CopyKey } from './i18n';
import { getMailState, isError, retryMail, setMailPreferences, type MailState } from './api';
import { reachFrom, type Reach } from './mailReach';

/**
 * Whether mail reaches this learner, and which of the parts they can stop they want
 * (F-106, and one switch per category since F-124).
 *
 * **The bouncing half is the reason this panel exists, and it is the only place a learner
 * can find it out.** `POST /api/auth/email/start` deliberately answers "we sent it" whether
 * or not anything was sent — telling a caller which addresses this account can reach would
 * disclose who is verified in an AWS account shared with a live business — so a learner
 * whose mailbox is refusing our mail sees a sign-in screen that looks like it worked and a
 * code that never arrives. Without this panel there is no difference, from where they are
 * standing, between a broken mailbox and a broken product.
 *
 * It shows the mail server's own words. "Mail to this address is bouncing" tells somebody
 * nothing they can act on; `550 5.1.1 user unknown` tells them the mailbox name is wrong,
 * and `452 over quota` tells them to empty it.
 *
 * **Nothing is rendered when the account has no address.** A panel about mail to an address
 * that does not exist is a panel with no true sentence in it, and the mail preference has
 * nowhere to be delivered either. It appears when `EmailAccess` above has been used.
 *
 * Beside `EmailAccess` rather than inside it, because that panel is a *form* for adding an
 * address and this is a *state* of one that exists — folding them together would mean the
 * form's stages had to account for a bounce, and a learner mid-way through typing a new
 * address would be told about the old one's mailbox.
 */

const BOUNCE_COPY: Record<string, CopyKey> = {
  hard_bounce: 'mail.bouncing.hard',
  complaint: 'mail.bouncing.complaint',
};

/**
 * One sentence per state of "can anything still reach you", and silence for the ordinary
 * one.
 *
 * A table rather than a chain of ternaries in the JSX, so that adding a state to `Reach`
 * without deciding what to say about it is visible here rather than rendering nothing. The
 * `push` case is deliberately empty: a learner with a device is not making themselves
 * unreachable, and a warning shown to everybody is a warning nobody reads on the one
 * screen where it is true.
 */
const REACH_COPY: Record<Reach, CopyKey | undefined> = {
  push: undefined,
  mail_only: 'mail.nudge.onlyChannel',
  nothing_switched_off: 'mail.nudge.unreachable',
  nothing_bouncing: 'mail.nudge.unreachableBouncing',
};

const RETRY_PROBLEM: Record<string, CopyKey> = {
  too_soon: 'mail.retry.tooSoon',
  not_suppressed: 'mail.retry.notSuppressed',
  no_address: 'mail.retry.noAddress',
};

export default function Mail() {
  const { t } = useI18n();
  const [state, setState] = useState<MailState>();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<CopyKey>();
  const [retried, setRetried] = useState(false);

  const fill = (key: CopyKey, values: Record<string, string>): string =>
    t(key).replace(/\{(\w+)\}/g, (_, name: string) => values[name] ?? '');

  const refresh = useCallback(async () => {
    const result = await getMailState();
    setLoading(false);
    // A failed read leaves `state` undefined, which renders nothing at all. That is the
    // right degradation: this panel is an explanation, and an explanation nobody can be
    // sure of is worse than silence on a screen that has a working sign-in on it already.
    if (isError(result)) return;
    setState(result);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /*
   * One save for both switches, and it sends only the one that moved (F-124).
   *
   * The opt-*out* is the stored field and each switch is phrased the other way round —
   * people turn things *on*, not off. Inverting here rather than in the API keeps the
   * stored meaning stable: a missing field means "not opted out", which is what makes
   * every learner who predates the switch reachable rather than silently unreachable, and
   * it is also the safe default for a value the bounce handler writes.
   *
   * Naming one category per call is what keeps the two independent all the way down. A
   * body carrying both would make this screen the place where turning off reports also
   * writes a value for nudges — the exact coupling F-124 exists to remove — using whatever
   * the screen last read, which on a stale tab is not what the account says.
   */
  async function save(wanted: { reportsOptOut?: boolean; nudgeOptOut?: boolean }): Promise<void> {
    setBusy(true);
    setProblem(undefined);
    const result = await setMailPreferences(wanted);
    setBusy(false);
    if (isError(result)) {
      setProblem('mail.error.generic');
      return;
    }
    setState(result);
  }

  async function tryAgain(): Promise<void> {
    setBusy(true);
    setProblem(undefined);
    setRetried(false);
    const result = await retryMail();
    setBusy(false);
    if (isError(result)) {
      setProblem(RETRY_PROBLEM[result.error ?? ''] ?? 'mail.error.generic');
      // The state may have moved under us — somebody signing in on their phone clears a
      // suppression too — so the refusal is followed by a re-read rather than leaving the
      // screen claiming something that is no longer true.
      void refresh();
      return;
    }
    setState(result);
    setRetried(true);
  }

  if (loading) {
    return (
      <section className="panel">
        <h2>{t('mail.title')}</h2>
        <p className="muted">{t('mail.checking')}</p>
      </section>
    );
  }

  if (!state?.email) return null;

  /*
   * Decided once, from the account's own answer, and not from this browser.
   *
   * `pushManager.getSubscription()` speaks for the device in front of them; the question is
   * whether *anything* can reach the person, and a phone they are not holding counts.
   */
  const reach: Reach = reachFrom(state);
  const reachCopy = REACH_COPY[reach];

  return (
    <section className="panel">
      <h2>{t('mail.title')}</h2>
      <p className="muted">{t('mail.body')}</p>

      {state.suppressed && (
        <div className="field">
          <p className="problem" role="alert">
            {fill(BOUNCE_COPY[state.reason ?? ''] ?? 'mail.bouncing.hard', { email: state.email })}
          </p>
          {state.diagnostic && (
            <small>{fill('mail.bouncing.diagnostic', { diagnostic: state.diagnostic })}</small>
          )}
          <div className="actions">
            <button
              type="button"
              className="button"
              onClick={() => void tryAgain()}
              // Offered only while the cooldown has passed. The limit is on the clearing
              // rather than on the sending because this is where it can be explained, and
              // one press spends one real bounce against a shared sending reputation.
              disabled={busy || !state.canRetry}
            >
              {busy ? t('mail.retry.working') : t('mail.retry')}
            </button>
            {retried && (
              <span className="muted" role="status">
                {t('mail.retry.done')}
              </span>
            )}
          </div>
        </div>
      )}

      {/*
        Two switches, one per category, and the order is deliberate.

        The nudge comes first because it is the one with a consequence: it is the mail sent
        when a question is waiting and no device can be notified, so for a learner who has
        not installed the app it is the only way nabu reaches them at all. Until F-124 it
        shared the reports switch, and turning off end-of-day summaries silently turned off
        being taught — a learner who had asked for less mail would simply have gone quiet,
        with nothing on this screen or anywhere else saying why. Leo, 2026-09-16: "make the
        fallback mail its own category, people should be able to turn it off but
        individually."
      */}
      <div className="form">
        <div className="field">
          <label className="switch">
            <input
              type="checkbox"
              checked={!state.nudgeOptOut}
              disabled={busy}
              onChange={(e) => void save({ nudgeOptOut: !e.target.checked })}
            />
            <span>{state.nudgeOptOut ? t('mail.nudge.off') : t('mail.nudge.on')}</span>
          </label>
          <small>{t('mail.nudge.label')}</small>
        </div>
        <small className="muted">{t('mail.nudge.hint')}</small>

        {/*
          The honest sentence, and it is shown to the people it is true about.

          A learner with no device we can notify who switches this off cannot be reached by
          this system at all: the question waits in the chat until they happen to open the
          app. That has to be said at the moment they do it — rule 4's spirit is that we do
          not quietly stop being able to teach somebody — and it has to be said only to
          them. Showing it to everybody would make it a nag nobody reads, and showing it to
          nobody is how somebody turns off their own teaching believing they declined a
          newsletter.

          `pushDevices` is the account's count and not this browser's, because a phone in
          another room still counts as reachable and `getSubscription()` here would say no.

          It states the consequence and stops. No confirmation dialog, no second press, no
          argument: somebody who says stop must be obeyed (CLAUDE.md rule 4), and a warning
          that reads as pressure is what makes a person reach for the spam button instead of
          the switch. `role="status"` rather than `alert` for the same reason — it is a fact
          they should hear, not an emergency.
        */}
        {reachCopy && (
          <small className="muted" role="status">
            {t(reachCopy)}
          </small>
        )}

        <div className="field">
          <label className="switch">
            <input
              type="checkbox"
              checked={!state.reportsOptOut}
              disabled={busy}
              onChange={(e) => void save({ reportsOptOut: !e.target.checked })}
            />
            <span>{state.reportsOptOut ? t('mail.reports.off') : t('mail.reports.on')}</span>
          </label>
          <small>{t('mail.reports.label')}</small>
        </div>
        <small className="muted">{t('mail.reports.hint')}</small>

        {/*
          What the link in the mail does, said where the switches are.

          One-click unsubscribe (RFC 8058) is a single action with no interface — the mail
          client posts to a URL and shows its own confirmation, and we never get to ask
          which of these two they meant — so it stops both. Somebody reading this screen is
          exactly the person who needs to know that the link is the blunt instrument and
          this is the fine one.
        */}
        <small className="muted">{t('mail.switches.unsubscribe')}</small>

        {/*
          And a bounce outranks both switches, which is only worth saying while one is in
          force. `mayMail` refuses a suppressed address whatever the preferences say, so a
          learner who has just turned everything back on after fixing nothing would
          otherwise be waiting for mail that is never going to arrive.

          Not when `reach` has already said it: a learner with no device *and* a bouncing
          address is told the stronger version above, and two sentences saying the same
          thing differently is how somebody decides the second one means something else.
        */}
        {state.suppressed && reach !== 'nothing_bouncing' && (
          <small className="muted">{t('mail.switches.suppressed')}</small>
        )}
      </div>

      {problem && (
        <p className="problem" role="alert">
          {t(problem)}
        </p>
      )}
    </section>
  );
}
